import React, { useState, useEffect } from 'react';
import { RefreshCw, Activity, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API = window.__WR_API_BASE__;

const timeAgo = (d) => {
  if (!d) return '';
  const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h`;
};

export const HealthPulse = ({ isAdmin }) => {
  const [stats, setStats] = useState(null);
  const [issues, setIssues] = useState([]);
  const [bugCounts, setBugCounts] = useState({ new: 0, investigating: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const navigate = useNavigate();

  const fetchHealth = async () => {
    try {
      const [statsRes, issuesRes, bugsRes] = await Promise.all([
        axios.get(`${API}/api/admin/sentry-stats`),
        axios.get(`${API}/api/admin/sentry-issues?project=frontend&limit=5`),
        axios.get(`${API}/api/admin/bug-reports?limit=1`).catch(() => ({ data: { counts: { new: 0, investigating: 0 } } })),
      ]);
      setStats(statsRes.data);
      setIssues(issuesRes.data.issues || []);
      setBugCounts(bugsRes.data?.counts || { new: 0, investigating: 0 });
      setLastCheck(new Date());
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchHealth();
    const timer = setInterval(fetchHealth, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [isAdmin]);

  if (!isAdmin || !stats) return null;

  const totalErrors = (stats.frontend?.errors || 0) + (stats.backend?.errors || 0);
  const totalIssues = (stats.frontend?.total || 0) + (stats.backend?.total || 0);
  const newBugs = bugCounts.new || 0;
  const combinedSeverity = totalErrors + (newBugs * 2);
  
  const iconColor = combinedSeverity === 0 
    ? 'text-emerald-400' 
    : combinedSeverity <= 3 
      ? 'text-amber-400'
      : 'text-red-400';
  
  const glowColor = combinedSeverity === 0
    ? 'drop-shadow-[0_0_4px_rgba(16,185,129,0.7)]'
    : combinedSeverity <= 3
      ? 'drop-shadow-[0_0_4px_rgba(245,158,11,0.7)]'
      : 'drop-shadow-[0_0_6px_rgba(239,68,68,0.8)]';

  const totalBadge = totalErrors + newBugs;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-secondary/50 transition-colors"
          data-testid="health-pulse-btn"
          title={`Admin · System Health: ${totalErrors} errors, ${newBugs} user reports`}
        >
          <Shield className={`w-4 h-4 ${iconColor} ${glowColor} ${combinedSeverity > 3 ? 'animate-pulse' : ''}`} />
          {totalBadge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-50" align="end" data-testid="health-pulse-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-foreground">System Health</span>
            {lastCheck && <span className="text-[9px] text-muted-foreground">{timeAgo(lastCheck.toISOString())} ago</span>}
          </div>
          <button onClick={fetchHealth} className="p-1 rounded hover:bg-secondary text-muted-foreground" title="Refresh">
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-border/50">
          <div className="text-center">
            <p className="text-lg font-bold text-red-400">{totalErrors}</p>
            <p className="text-[9px] text-muted-foreground">Errors</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-amber-400">{(stats.frontend?.warnings || 0) + (stats.backend?.warnings || 0)}</p>
            <p className="text-[9px] text-muted-foreground">Warnings</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground">{totalIssues}</p>
            <p className="text-[9px] text-muted-foreground">Total</p>
          </div>
        </div>

        <div className="max-h-[200px] overflow-y-auto">
          {issues.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-emerald-400">All clear - no unresolved issues</div>
          ) : (
            issues.map(issue => (
              <a
                key={issue.id}
                href={issue.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 px-3 py-2 hover:bg-secondary/50 transition-colors border-b border-border/30 last:border-0"
              >
                <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                  issue.level === 'error' ? 'bg-red-500' : issue.level === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-foreground truncate">{issue.title}</p>
                  <div className="flex items-center gap-2 text-[9px] text-muted-foreground mt-0.5">
                    <span>{issue.count} events</span>
                    <span>{timeAgo(issue.lastSeen)}</span>
                  </div>
                </div>
              </a>
            ))
          )}
        </div>

        <div className="px-3 py-2 border-t border-border bg-secondary/20">
          <button
            onClick={() => { setIsOpen(false); navigate('/admin'); }}
            className="w-full flex items-center justify-center gap-1.5 text-[10px] text-primary hover:text-primary/80 font-medium transition-colors"
            data-testid="health-open-admin"
          >
            <Shield className="w-3 h-3" />
            Open Admin Portal
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const AdminMenuLink = ({ navigate }) => {
  const [errorCount, setErrorCount] = useState(0);
  
  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/sentry-stats`);
        const total = (res.data.frontend?.errors || 0) + (res.data.backend?.errors || 0);
        setErrorCount(total);
      } catch { /* ignore */ }
    };
    check();
  }, []);

  return (
    <button 
      onClick={() => navigate('/admin')} 
      className={`w-full flex items-center gap-2.5 px-2 py-2 text-sm rounded-md transition-colors ${
        errorCount > 0 
          ? 'text-red-400 hover:bg-red-500/10 animate-pulse' 
          : 'text-muted-foreground hover:bg-secondary'
      }`}
      data-testid="menu-admin"
    >
      <Shield className={`w-4 h-4 ${errorCount > 0 ? 'text-red-400' : ''}`} />
      Admin Portal
      {errorCount > 0 && (
        <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[9px] font-bold">
          {errorCount}
        </span>
      )}
    </button>
  );
};

export default HealthPulse;
